import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
} from '@/components/ui/drawer';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, Save, Search, Loader2 } from 'lucide-react';
import MediaUploader from '@/components/admin/MediaUploader';
import MultiImageUploader from '@/components/admin/MultiImageUploader';
import RichTextEditor from '@/components/admin/RichTextEditor';
import StatsListEditor from '@/components/admin/StatsListEditor';
import VideoListEditor from '@/components/admin/VideoListEditor';

/**
 * Generic CRUD manager for admin content.
 *
 * Props:
 * - resource: string (e.g. 'services', 'projects', 'testimonials', 'news', 'gallery', 'partners', 'downloads', 'impact-stats', 'media-awards')
 * - title: string
 * - description: string
 * - fields: [ { key, label, type: 'text'|'textarea'|'select'|'number'|'image'|'checkbox'|'array', options?: [{value,label}], placeholder? } ]
 * - columns: [ { key, label, render?: (row)=>node, primary?: boolean } ]
 * - defaultItem: object
 * - searchKeys: array of keys to search on
 */
export const CrudManager = ({
    resource,
    title,
    description,
    fields,
    columns,
    defaultItem = {},
    searchKeys = ['title', 'name'],
    beforeEdit,
    beforeSave,
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null | object
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminApi.list(resource);
            setItems(data);
        } catch {
            toast.error('Failed to load. Are you signed in as admin?');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resource]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter((it) =>
            searchKeys.some((k) =>
                String(it[k] || '')
                    .toLowerCase()
                    .includes(s)
            )
        );
    }, [items, search, searchKeys]);

    const openCreate = () => {
        setEditing({ ...defaultItem });
        setDrawerOpen(true);
    };
    const openEdit = (row) => {
        const seeded = beforeEdit ? beforeEdit({ ...row }) : { ...row };
        setEditing(seeded);
        setDrawerOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const isEdit = !!editing?.id;
            let payload = { ...editing };
            // Post-process array-type fields (comma-separated string -> array)
            fields.forEach((f) => {
                if (f.type === 'array' && typeof payload[f.key] === 'string') {
                    payload[f.key] = payload[f.key]
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean);
                }
                if (f.type === 'images-list' && !Array.isArray(payload[f.key])) {
                    payload[f.key] = [];
                }
                if (f.type === 'videos-list' && !Array.isArray(payload[f.key])) {
                    payload[f.key] = [];
                }
                if (f.type === 'stats-list' && !Array.isArray(payload[f.key])) {
                    payload[f.key] = [];
                }
                if (f.type === 'number' && payload[f.key] !== undefined && payload[f.key] !== '') {
                    payload[f.key] = Number(payload[f.key]);
                }
            });
            // Apply optional caller transform (e.g. merge helper fields)
            if (beforeSave) {
                payload = beforeSave(payload);
            }
            if (isEdit) {
                const { id, ...rest } = payload;
                await adminApi.update(resource, id, rest);
                toast.success('Updated');
            } else {
                await adminApi.create(resource, payload);
                toast.success('Created');
            }
            setDrawerOpen(false);
            setEditing(null);
            await load();
        } catch (e) {
            toast.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        try {
            await adminApi.remove(resource, confirmDelete.id);
            toast.success('Deleted');
            setConfirmDelete(null);
            await load();
        } catch {
            toast.error('Delete failed');
        }
    };

    const renderField = (f) => {
        const value = editing?.[f.key];
        const onChange = (v) =>
            setEditing((e) => ({ ...e, [f.key]: v }));
        const display =
            f.type === 'array' && Array.isArray(value) ? value.join(', ') : value ?? '';
        switch (f.type) {
            case 'textarea':
                return (
                    <Textarea
                        rows={f.rows || 4}
                        value={display}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`admin-field-${f.key}`}
                    />
                );
            case 'rich-text':
                return (
                    <RichTextEditor
                        value={value || ''}
                        onChange={onChange}
                        placeholder={f.placeholder || 'Write content…'}
                        minHeight={f.minHeight || 240}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'upload-image':
                return (
                    <MediaUploader
                        value={value || ''}
                        onChange={onChange}
                        kind="image"
                        prefix={f.prefix}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'upload-pdf':
                return (
                    <MediaUploader
                        value={value || ''}
                        onChange={onChange}
                        kind="pdf"
                        prefix={f.prefix}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'images-list':
                return (
                    <MultiImageUploader
                        value={Array.isArray(value) ? value : []}
                        onChange={onChange}
                        prefix={f.prefix}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'stats-list':
                return (
                    <StatsListEditor
                        value={Array.isArray(value) ? value : []}
                        onChange={onChange}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'videos-list':
                return (
                    <VideoListEditor
                        value={Array.isArray(value) ? value : []}
                        onChange={onChange}
                        prefix={f.prefix}
                        testId={`admin-field-${f.key}`}
                    />
                );
            case 'select':
                return (
                    <Select
                        value={display?.toString()}
                        onValueChange={(v) => onChange(v)}
                    >
                        <SelectTrigger data-testid={`admin-field-${f.key}`}>
                            <SelectValue placeholder={f.placeholder || 'Select'} />
                        </SelectTrigger>
                        <SelectContent>
                            {(f.options || []).map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            case 'checkbox':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                            className="h-4 w-4"
                            data-testid={`admin-field-${f.key}`}
                        />
                        <span className="text-sm text-muted-foreground">
                            {f.checkboxLabel || f.label}
                        </span>
                    </div>
                );
            case 'number':
                return (
                    <Input
                        type="number"
                        value={display}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`admin-field-${f.key}`}
                    />
                );
            case 'array':
                return (
                    <Textarea
                        rows={2}
                        value={display}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={f.placeholder || 'comma-separated values'}
                        data-testid={`admin-field-${f.key}`}
                    />
                );
            default:
                return (
                    <Input
                        value={display}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`admin-field-${f.key}`}
                    />
                );
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold">{title}</h1>
                    {description && (
                        <p className="text-sm text-muted-foreground">{description}</p>
                    )}
                </div>
                <Button
                    onClick={openCreate}
                    className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                    data-testid={`admin-${resource}-new-button`}
                >
                    <Plus className="h-4 w-4 mr-1.5" /> New
                </Button>
            </div>

            <Card className="bg-card border-border">
                <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="pl-9"
                                data-testid={`admin-${resource}-search`}
                            />
                        </div>
                        <Badge variant="outline" className="ml-auto">
                            {filtered.length} items
                        </Badge>
                    </div>

                    {loading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={`admin-${resource}-table`}>
                                <TableHeader>
                                    <TableRow>
                                        {columns.map((c) => (
                                            <TableHead key={c.key}>{c.label}</TableHead>
                                        ))}
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={columns.length + 1}
                                                className="text-center py-8 text-muted-foreground text-sm"
                                            >
                                                No items yet. Click &quot;New&quot; to add one.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filtered.map((row) => (
                                            <TableRow key={row.id}>
                                                {columns.map((c) => (
                                                    <TableCell
                                                        key={c.key}
                                                        className={
                                                            c.primary ? 'font-medium' : ''
                                                        }
                                                    >
                                                        {c.render
                                                            ? c.render(row)
                                                            : row[c.key]}
                                                    </TableCell>
                                                ))}
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openEdit(row)}
                                                        data-testid={`admin-${resource}-edit-${row.id}`}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setConfirmDelete(row)}
                                                        className="text-destructive hover:text-destructive"
                                                        data-testid={`admin-${resource}-delete-${row.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Drawer */}
            <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DrawerContent className="max-h-[92vh]">
                    <DrawerHeader>
                        <DrawerTitle>
                            {editing?.id ? `Edit ${title.slice(0, -1) || 'item'}` : `New ${title.slice(0, -1) || 'item'}`}
                        </DrawerTitle>
                        <DrawerDescription>
                            Fill in the details and save to publish.
                        </DrawerDescription>
                    </DrawerHeader>
                    <div className="px-6 pb-4 overflow-y-auto space-y-4 max-h-[68vh]">
                        {fields.map((f) => (
                            <div key={f.key}>
                                <Label className="mb-1.5 block">{f.label}</Label>
                                {renderField(f)}
                                {f.hint && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {f.hint}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <DrawerFooter className="flex-row justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDrawerOpen(false)}
                            data-testid={`admin-${resource}-cancel-button`}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                            data-testid={`admin-${resource}-save-button`}
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4 mr-1.5" />
                            )}
                            Save
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Delete confirm */}
            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`admin-${resource}-delete-cancel`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`admin-${resource}-delete-confirm`}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CrudManager;
